import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Navbar } from "./navbar";
import { Contact } from "./contact";
import { addToPanier, updateClimatisseur, updateCartItem, toggleCommentInteraction, addComment, deleteComment } from "./action";
import { LikeCommentaire, DislikeCommentaire, AjouterCommentaire, SupprimerCommentaire, toggleFavoris, Ajouter } from "./slice";
import { patchCompte } from "./action";
import {
    X,
    ShoppingCart,
    ArrowLeft,
    Star,
    ThumbsUp,
    ThumbsDown,
    MessageSquare,
    Trash2,
    Clock,
    CheckCircle2,
    CheckCircle,
    Heart,
    User
} from "lucide-react";
import "../styles/infoClimatisseur.css";
import API_BASE_URL from "../config";

export function InfoClimatisseur() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    const listC = useSelector(state => state.listC) || [];
    const panier = useSelector(state => state.panier) || [];
    const favoris = useSelector(state => state.favoris) || [];
    const comptes = useSelector(state => state.comptes) || [];
    
    const [user, setUser] = useState(null);
    const hasCheckedNav = React.useRef(false);
    useEffect(() => {
        // Run ONCE only - avoids re-triggering after Redux state changes (e.g. comment deletion)
        if (!hasCheckedNav.current) {
            hasCheckedNav.current = true;
            if (!location.state?.fromList) {
                navigate('/listeClimatisseurs', { replace: true });
                return;
            }
        }
        const loggedUser = localStorage.getItem('user');
        if (loggedUser) {
            setUser(JSON.parse(loggedUser));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const climatisseur = listC.find(c => c.id == id);
    const cartItemId = location.state?.cartItemId;
    const cartItem = cartItemId ? panier.find(pi => pi.id === cartItemId) : null;

    // Aggregated color map from all items in listC
    const colorMap = React.useMemo(() => {
        const map = {
            'blanc': '#ffffff', 'noir': '#000000', 'noire': '#000000',
            'noir mat': '#222222', 'gris': '#808080', 'gris anthracite': '#2f3e46',
            'argent': '#C0C0C0', 'argenté': '#C0C0C0', 'inox': '#8e9aaf',
            'bleu': '#0000FF', 'rouge': '#FF0000', 'vert': '#008000',
            'or': '#FFD700', 'doré': '#FFD700', 'beige': '#F5F5DC',
            'black': '#000000', 'white': '#ffffff', 'grey': '#808080',
            'gray': '#808080', 'silver': '#C0C0C0', 'blue': '#0000FF',
            'red': '#FF0000', 'green': '#008000', 'gold': '#FFD700'
        };

        listC.forEach(clim => {
            (clim.colors || []).forEach(c => {
                if (typeof c === 'object' && c.color && c.hex) {
                    map[c.color.trim().toLowerCase()] = c.hex;
                }
            });
        });
        return map;
    }, [listC]);

    const [selectedSizeIndex, setSelectedSizeIndex] = useState(0);
    const [selectedColor, setSelectedColor] = useState("Standard");

    useEffect(() => {
        if (cartItem) {
            const sIndex = climatisseur?.tailles.findIndex(t => t.taille === cartItem.taille);
            if (sIndex !== -1) setSelectedSizeIndex(sIndex);
            setSelectedColor(cartItem.color || "Standard");
        } else if (climatisseur?.colors && climatisseur.colors.length > 0) {
            const firstColor = climatisseur.colors[0];
            setSelectedColor(typeof firstColor === 'string' ? firstColor : firstColor?.color || "Standard");
        }
    }, [climatisseur, cartItem]);

    const [showCommentForm, setShowCommentForm] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [toastMsg, setToastMsg] = useState("");

    // Comment form state
    const [commentText, setCommentText] = useState("");
    const [commentRating, setCommentRating] = useState(5);
    const [confirmDeleteInfo, setConfirmDeleteInfo] = useState(null);
    const [deleteError, setDeleteError] = useState('');

    // Handle scroll lock when modal-like page is open
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);

    const scrollRef = React.useRef(null);
    const [isPaused, setIsPaused] = useState(false);

    const visibleComments = React.useMemo(() => 
        (climatisseur?.commantaires || []).filter(c => c.status === 'validé'),
        [climatisseur?.commantaires]
    );

    useEffect(() => {
        if (!visibleComments || visibleComments.length <= 1 || isPaused) return;

        let animationFrameId;
        const scroll = () => {
            if (scrollRef.current) {
                scrollRef.current.scrollLeft += 0.8; // Smooth slow movement
                
                // Infinite loop logic: when reaching the end of the first set, jump back to start
                const firstSetWidth = (scrollRef.current.scrollWidth / 2);
                if (scrollRef.current.scrollLeft >= firstSetWidth) {
                    scrollRef.current.scrollLeft = 0;
                }
            }
            animationFrameId = requestAnimationFrame(scroll);
        };

        animationFrameId = requestAnimationFrame(scroll);
        return () => cancelAnimationFrame(animationFrameId);
    }, [visibleComments, isPaused]);

    if (!climatisseur) {
        return (
            <div className="info-modal-overlay">
                <div className="info-modal-content" style={{ justifyContent: 'center', alignItems: 'center', padding: '50px' }}>
                    <h2>Produit non trouvé</h2>
                    <Link to="/" className="cancel-btn">Retour à l'accueil</Link>
                </div>
            </div>
        );
    }

    const currentTaille = climatisseur.tailles[selectedSizeIndex] || climatisseur.tailles[0];

    // Handle both camelCase (proprietesTechniques) and snake_case (proprietes_techniques) from API
    const getTechProps = (taille) => {
        return taille?.proprietesTechniques 
            || taille?.proprietes_techniques 
            || [];
    };

    // Handle valeur which can be a JSON array, string, or number
    const displayValeur = (valeur) => {
        if (valeur === null || valeur === undefined) return 'N/A';
        if (Array.isArray(valeur)) return valeur.join(', ');
        if (typeof valeur === 'object') return JSON.stringify(valeur);
        return String(valeur);
    };

    const handleAddToCart = () => {
        if (!user) {
            console.warn("Veuillez vous connecter pour ajouter au panier.");
            navigate("/login");
            return;
        }

        const targetEmail = user.email;

        // Find correct image for the selected color
        const currentColorObj = climatisseur.colors?.find(c =>
            (typeof c === 'string' ? c : c.color) === selectedColor
        );
        const displayImage = (typeof currentColorObj === 'object' && currentColorObj?.image)
            ? currentColorObj.image
            : climatisseur.image;

        // Check if an identical item already exists in the cart for this user
        const existingItem = panier.find(item =>
            (String(item.originalId) === String(climatisseur.id) || String(item.idClimatisseur) === String(climatisseur.id)) &&
            item.taille === currentTaille.taille &&
            item.color === selectedColor &&
            Number(item.prix) === Number(currentTaille.prix)
        );

        if (cartItem) {
            // Update specific item (Editing mode from cart)
            const updatedItem = {
                ...cartItem,
                taille: currentTaille.taille,
                prix: currentTaille.prix,
                color: selectedColor,
                image: displayImage,
                userEmail: targetEmail
            };
            const newPanier = panier.map(p => p.id === updatedItem.id ? updatedItem : p);
            dispatch(patchCompte({ id: user.id, panier: newPanier }));
            setToastMsg(`${climatisseur.nomClimatisseur} mis à jour !`);
        } else if (existingItem) {
            // Item already in cart with same options -> increment quantity
            const updatedItem = {
                ...existingItem,
                quantite: (existingItem.quantite || 1) + 1
            };
            const newPanier = panier.map(p => p.id === updatedItem.id ? updatedItem : p);
            dispatch(patchCompte({ id: user.id, panier: newPanier }));
            setToastMsg(`Quantité de ${climatisseur.nomClimatisseur} augmentée !`);
        } else {
            // New item completely
            const newItem = {
                id: Date.now().toString() + "_" + Math.random().toString(36).substr(2, 9),
                originalId: climatisseur.id,
                nomClimatisseur: climatisseur.nomClimatisseur,
                image: displayImage,
                taille: currentTaille.taille,
                prix: currentTaille.prix,
                quantite: 1,
                checked: true,
                userEmail: targetEmail,
                color: selectedColor
            };
            const newPanier = [...panier, newItem];
            dispatch(Ajouter(newItem)); 
            dispatch(patchCompte({ id: user.id, panier: newPanier }));
            setToastMsg(`${climatisseur.nomClimatisseur} ajouté au panier !`);
        }

        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
    };

    const handleLike = (comId) => {
        if (!user) {
            return;
        }
        dispatch(LikeCommentaire({ climatiseurId: climatisseur.id, commentaireId: comId, userEmail: user.email }));
        dispatch(toggleCommentInteraction({ id: comId, userEmail: user.email, type: 'like' }));
    };

    const handleDislike = (comId) => {
        if (!user) {
            return;
        }
        dispatch(DislikeCommentaire({ climatiseurId: climatisseur.id, commentaireId: comId, userEmail: user.email }));
        dispatch(toggleCommentInteraction({ id: comId, userEmail: user.email, type: 'dislike' }));
    };

    const submitComment = (e) => {
        e.preventDefault();
        if (!user) {
            navigate("/login");
            return;
        }
        if (!commentText.trim()) return;

        const commentPayload = {
            climatiseur_id: climatisseur.id,
            username: user.name || user.email.split('@')[0],
            userEmail: user.email,
            commantaire: commentText,
            avis: `${commentRating} ${commentRating === 1 ? 'étoile' : 'étoiles'}`,
            status: 'en attente'
        };

        dispatch(addComment(commentPayload))
            .unwrap()
            .then((newComment) => {
                dispatch(AjouterCommentaire({ climatiseurId: climatisseur.id, commentaire: newComment }));
                setToastMsg("Merci pour votre avis !");
                setShowToast(true);
                setTimeout(() => setShowToast(false), 3000);
            })
            .catch(err => {
                console.error("Failed to add comment:", err);
            });

        setCommentText("");
        setShowCommentForm(false);
    };

    const handleDeleteComment = (comId) => {
        setConfirmDeleteInfo(comId);
        setDeleteError('');
    };

    const confirmDeleteAction = () => {
        if (confirmDeleteInfo) {
            setDeleteError('');
            dispatch(deleteComment(confirmDeleteInfo))
                .unwrap()
                .then(() => {
                    dispatch(SupprimerCommentaire({ climatiseurId: climatisseur.id, commentaireId: confirmDeleteInfo }));
                    setConfirmDeleteInfo(null);
                })
                .catch(err => {
                    console.error("Failed to delete comment:", err);
                    const msg = err?.response?.data?.message || err?.message || "Erreur lors de la suppression.";
                    setDeleteError(`Erreur: ${msg}`);
                });
        }
    };

    const handleToggleFavoris = () => {
        if (!user) {
            navigate("/login");
            return;
        }

        const isFavorite = favoris.includes(climatisseur.id);
        const newFavoris = isFavorite
            ? favoris.filter(id => id !== climatisseur.id)
            : [...favoris, climatisseur.id];

        dispatch(toggleFavoris(climatisseur.id));
        dispatch(patchCompte({ id: user.id, favoris: newFavoris }));
    };

    const handleClose = () => {
        navigate(-1);
    }

    return (
        <div className="info-modal-overlay" onClick={handleClose}>
            {showToast && (
                <div className="toast-notification">
                    <CheckCircle2 size={20} style={{ marginRight: '10px' }} />
                    {toastMsg}
                </div>
            )}

            <div className="info-modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="close-btn" onClick={handleClose} title="Fermer">
                    <X />
                </button>

                <div className="modal-image-side">
                    {/* Dynamic image based on selected color */}
                    {(() => {
                        const currentColorObj = climatisseur.colors?.find(c =>
                            (typeof c === 'string' ? c : c.color) === selectedColor
                        );
                        const displayImage = (typeof currentColorObj === 'object' && currentColorObj?.image)
                            ? currentColorObj.image
                            : climatisseur.image;

                        return <img src={displayImage} alt={climatisseur.nomClimatisseur} />;
                    })()}
                </div>

                <div className="modal-details-side">
                    <h2>{climatisseur.nomClimatisseur}</h2>
                    <p className="price-tag">{currentTaille.prix.toLocaleString()} MAD</p>

                    <p className="description-text">
                        {climatisseur.description || "Le climatiseur " + climatisseur.nomClimatisseur + " offre une performance de refroidissement rapide et une efficacité énergétique exceptionnelle. Avec sa technologie avancée, il est silencieux et économique."}
                    </p>

                    <div className="option-group">
                        <label>TAILLE DISPONIBLE</label>
                        <select
                            className="modal-select"
                            value={selectedSizeIndex}
                            onChange={(e) => setSelectedSizeIndex(parseInt(e.target.value))}
                        >
                            {climatisseur.tailles.map((t, index) => (
                                <option key={index} value={index}>{t.taille}</option>
                            ))}
                        </select>
                    </div>

                    {climatisseur.colors && climatisseur.colors.length > 0 && (
                        <div className="option-group">
                            <label>COULEURS DISPONIBLES</label>
                            <div className="color-options-container">
                                {climatisseur.colors.map((color, idx) => {
                                    const colorName = typeof color === 'string' ? color : color?.color || `Color ${idx + 1}`;
                                    const normalized = colorName.trim().toLowerCase();
                                    const bgColor = colorMap[normalized] || (typeof color === 'object' ? color?.hex : null) || normalized || "#ffffff";

                                    return (
                                        <div
                                            key={idx}
                                            className={`color-square ${selectedColor === colorName ? 'selected' : ''}`}
                                            style={{ backgroundColor: bgColor }}
                                            onClick={() => setSelectedColor(colorName)}
                                            title={colorName}
                                        >
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className="option-group" style={{ marginTop: '20px' }}>
                        <label>PROPRIÉTÉS TECHNIQUES</label>
                        <div style={{ display: 'flex', flexDirection: 'column', marginTop: '10px' }}>
                            {getTechProps(currentTaille).length > 0 ? (
                                getTechProps(currentTaille).map((prop, idx) => (
                                    <div key={idx} style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '12px 0',
                                        borderBottom: '1px solid #f0f0f0',
                                        gap: '20px'
                                    }}>
                                        <span style={{
                                            fontWeight: '600',
                                            color: '#000033',
                                            fontSize: '0.95rem',
                                            flexShrink: 0
                                        }}>{prop.propriete}</span>
                                        <span style={{
                                            color: '#666',
                                            fontSize: '0.9rem',
                                            textAlign: 'right'
                                        }}>{displayValeur(prop.valeur)}</span>
                                    </div>
                                ))
                            ) : (
                                <p style={{ color: '#aaa', fontStyle: 'italic', fontSize: '0.85rem' }}>Aucune propriété technique disponible.</p>
                            )}
                        </div>
                    </div>

                    <div className="option-group">
                        <label>DÉTAILS SUR LA TAILLE (DIMENSIONS)</label>
                        <div className="size-details-box">
                            <div className="size-detail-item">
                                <span className="size-detail-key">LONGUEUR :</span>
                                <span className="size-detail-value">{currentTaille.details?.longueur || "68 cm"}</span>
                            </div>
                            <div className="size-detail-item">
                                <span className="size-detail-key">LARGEUR :</span>
                                <span className="size-detail-value">{currentTaille.details?.largeur || "24 cm"}</span>
                            </div>
                            <div className="size-detail-item">
                                <span className="size-detail-key">SOMK :</span>
                                <span className="size-detail-value">{currentTaille.details?.somk || "17 cm"}</span>
                            </div>
                        </div>
                    </div>

                    <div className="comments-section">
                        <div className="comments-header">
                            <h4>COMMENTAIRES</h4>
                            <button className="add-comment-btn" onClick={() => {
                                if (!user) {
                                    navigate("/login");
                                    return;
                                }
                                navigate(`/ajouterCommantaire/${climatisseur.id}`);
                            }}>
                                Ajouter
                            </button>
                        </div>

                        <div 
                            className={`comments-list-simple ${visibleComments.length > 1 ? 'has-fade' : ''}`}
                            ref={scrollRef}
                            onMouseEnter={() => setIsPaused(true)}
                            onMouseLeave={() => setIsPaused(false)}
                        >
                            {visibleComments.length === 0 ? (
                                <div className="no-comments-placeholder">
                                    <MessageSquare size={30} />
                                    <p>Soyez le premier à donner votre avis !</p>
                                </div>
                            ) : (
                                // Duplicate comments for infinite loop effect only if there are multiple comments
                                (visibleComments.length === 1 ? visibleComments : [...visibleComments, ...visibleComments])
                                    .map((com, idx) => (
                                        <div key={`${com.id}-${idx}`} className="comment-card">
                                            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                                                <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                                                    {(() => {
                                                        const commentUser = comptes.find(u => u.username === com.username || u.email.split('@')[0] === com.username);
                                                        if (commentUser?.image_user) {
                                                            return <img src={commentUser.image_user?.startsWith('data:image') ? commentUser.image_user : `${API_BASE_URL.replace('/api', '')}/storage/photos/${commentUser.image_user}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />;
                                                        }
                                                        return (
                                                             <span style={{ fontWeight: '800', fontSize: '1rem', color: 'var(--admin-secondary)', textTransform: 'uppercase' }}>
                                                                 {com.username ? com.username.charAt(0) : 'U'}
                                                             </span>
                                                         );
                                                    })()}
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <div className="comment-header-row">
                                                        <h6 style={{ margin: 0 }}>{com.username}</h6>
                                                        <div style={{ display: 'flex', gap: '2px' }}>
                                                            {[...Array(5)].map((_, i) => (
                                                                <Star
                                                                    key={i}
                                                                    size={12}
                                                                    fill={i < parseInt(com.avis) ? "#ffc107" : "none"}
                                                                    color={i < parseInt(com.avis) ? "#ffc107" : "#ccc"}
                                                                />
                                                            ))}
                                                        </div>
                                                    </div>
                                                    
                                                    <p className="comment-text" style={{ wordWrap: 'break-word', overflowWrap: 'break-word', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{com.commantaire}</p>
                                                    
                                                    <div className="comment-actions">
                                                        <button
                                                            className={`reaction-btn ${com.likedBy?.includes(user?.email) ? 'active-like' : ''}`}
                                                            onClick={() => handleLike(com.id)}
                                                        >
                                                            <ThumbsUp size={14} /> <span>{com.like || 0}</span>
                                                        </button>
                                                        <button
                                                            className={`reaction-btn ${com.dislikedBy?.includes(user?.email) ? 'active-dislike' : ''}`}
                                                            onClick={() => handleDislike(com.id)}
                                                        >
                                                            <ThumbsDown size={14} /> <span>{com.dislike || 0}</span>
                                                        </button>
                                                        
                                                        {user && (user.name === com.username || user.email.startsWith(com.username)) && (
                                                            <button 
                                                                className="btn-icon" 
                                                                style={{ padding: '5px', marginLeft: 'auto', border: 'none', background: 'transparent' }} 
                                                                onClick={() => handleDeleteComment(com.id)}
                                                                title="Supprimer mon avis"
                                                            >
                                                                <Trash2 size={16} color="#ef4444" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                            )}
                        </div>
                    </div>

                    <div className="modal-actions">
                        <button className="buy-btn" onClick={handleAddToCart}>
                            <ShoppingCart size={20} style={{ marginRight: '10px' }} />
                            Ajouter au Panier
                        </button>
                        <button className="cancel-btn" onClick={handleClose}>
                            Annuler
                        </button>
                    </div>
                </div>

                {/* Floating Favorite Button */}
                <button 
                    className={`btn-favorite-detail ${favoris.includes(climatisseur.id) ? 'active' : ''}`}
                    onClick={handleToggleFavoris}
                    title={favoris.includes(climatisseur.id) ? "Retirer des favoris" : "Ajouter aux favoris"}
                >
                    <Heart size={24} fill={favoris.includes(climatisseur.id) ? "currentColor" : "none"} />
                </button>
            </div>

            {/* Confirmation Delete Modal */}
            {confirmDeleteInfo && (
                <div onClick={(e) => e.stopPropagation()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
                    <style>{`
                        @keyframes shakeError {
                            0%, 100% { transform: translateX(0); }
                            20%, 60% { transform: translateX(-5px); }
                            40%, 80% { transform: translateX(5px); }
                        }
                    `}</style>
                    <div style={{ background: 'var(--admin-card-bg, #fff)', borderRadius: '16px', padding: '36px', maxWidth: '420px', width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', border: '1px solid var(--admin-border, #eee)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '18px' }}>
                            <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(239,68,68,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Trash2 size={22} color="#ef4444" />
                            </div>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '1.05rem', color: 'var(--admin-text-main, #333)', fontWeight: 700 }}>Supprimer le commentaire</h3>
                                <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--admin-text-muted, #666)' }}>Cette action est irréversible.</p>
                            </div>
                        </div>

                        {deleteError && (
                            <div style={{ background: '#fee2e2', color: '#dc2626', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', fontSize: '0.85rem', fontWeight: 600, animation: 'shakeError 0.4s ease-in-out', border: '1px solid #fca5a5' }}>
                                {deleteError}
                            </div>
                        )}

                        <p style={{ fontSize: '0.9rem', color: 'var(--admin-text-main, #333)', marginBottom: '24px', lineHeight: 1.6 }}>
                            Êtes-vous sûr de vouloir supprimer ce commentaire ?
                        </p>
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <button onClick={() => { setConfirmDeleteInfo(null); setDeleteError(''); }} style={{ padding: '10px 22px', borderRadius: '8px', border: '1px solid var(--admin-border, #eee)', background: 'transparent', color: 'var(--admin-text-main, #333)', cursor: 'pointer', fontWeight: 600 }}>Annuler</button>
                            <button onClick={confirmDeleteAction} style={{ padding: '10px 22px', borderRadius: '8px', border: 'none', background: '#ef4444', color: '#fff', cursor: 'pointer', fontWeight: 700 }}>Supprimer définitivement</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
